import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import DescriptionIcon from '@mui/icons-material/Description';
import { Actions } from '../actions';
import styles from './style';
import axios from '../../services/axios';

export default function SingleMasavExport({
  onMount,
  loading,
  masavExport
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = sessionStorage.getItem("userRole");

  useEffect(() => {
    dispatch(Actions.ACTIVE_TAB_CHANGED(11));
    onMount(id);
  }, [dispatch, id]);

  if (loading || !masavExport) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  const isConfirmed = masavExport.status === 'confirmed';
  const exportDate = new Date(masavExport.exportDate);
  const confirmationDate = masavExport.confirmationDate ? new Date(masavExport.confirmationDate) : null;

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(`/inquiries/masav-exports/${id}/csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `masav_export_${id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  const handleDownloadConfirmation = async () => {
    try {
      // Get file extension from confirmationFileKey
      let fileExtension = 'jpg'; // Default for old files without extension
      if (masavExport.confirmationFileKey) {
        const parts = masavExport.confirmationFileKey.split('.');
        if (parts.length > 1) {
          fileExtension = parts[parts.length - 1].toLowerCase();
        }
      }
      
      const response = await axios.get(`/inquiries/masav-exports/${id}/confirmation`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `masav_confirmation_${id}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading confirmation:', error);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/masav-exports')}
          sx={styles.backButton}
        >
          חזרה לארכיון
        </Button>
        <Typography variant="h5" sx={styles.title}>
          פרטי ייצוא מסב #{masavExport.id}
        </Typography>
      </Box>

      <Paper sx={styles.paper}>
        <Box sx={styles.statusSection}>
          <Chip
            icon={isConfirmed ? <CheckCircleIcon /> : <PendingIcon />}
            label={isConfirmed ? 'אושר' : 'ממתין לאישור'}
            color={isConfirmed ? 'success' : 'warning'}
            sx={styles.statusChip}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={styles.infoBox}>
              <Typography variant="subtitle2" sx={styles.label}>
                תאריך ייצוא
              </Typography>
              <Typography variant="body1">
                {`${exportDate.getDate()}/${exportDate.getMonth() + 1}/${exportDate.getFullYear()} ${exportDate.getHours()}:${exportDate.getMinutes().toString().padStart(2, '0')}`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={styles.infoBox}>
              <Typography variant="subtitle2" sx={styles.label}>
                מספר פניות
              </Typography>
              <Typography variant="body1">
                {masavExport.inquiriesCount}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={styles.infoBox}>
              <Typography variant="subtitle2" sx={styles.label}>
                סכום כולל
              </Typography>
              <Typography variant="body1">
                ₪{masavExport.totalAmount.toLocaleString()}
              </Typography>
            </Box>
          </Grid>

          {confirmationDate && (
            <Grid item xs={12} md={6}>
              <Box sx={styles.infoBox}>
                <Typography variant="subtitle2" sx={styles.label}>
                  תאריך אישור
                </Typography>
                <Typography variant="body1">
                  {`${confirmationDate.getDate()}/${confirmationDate.getMonth() + 1}/${confirmationDate.getFullYear()}`}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={styles.actionsSection}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            קבצים
          </Typography>
          <Box sx={styles.fileButtons}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              sx={styles.downloadButton}
            >
              הורד CSV מקורי
            </Button>

            {isConfirmed && masavExport.confirmationFileKey && (
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={handleDownloadConfirmation}
                sx={styles.downloadButton}
                color="success"
              >
                הורד קובץ אישור
              </Button>
            )}

            {!isConfirmed && role !== 'recruiting' && (
              <Button
                variant="contained"
                startIcon={<FileUploadIcon />}
                onClick={() => navigate(`/masav-exports/${id}/upload`)}
                color="primary"
              >
                העלה אישור
              </Button>
            )}
          </Box>
        </Box>

        {masavExport.inquiries && masavExport.inquiries.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={styles.inquiriesSection}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                פניות בייצוא ({masavExport.inquiries.length})
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">מספר פנייה</TableCell>
                      <TableCell align="right">שם מלא</TableCell>
                      <TableCell align="right">סכום</TableCell>
                      <TableCell align="right">סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {masavExport.inquiries.map((inquiry) => (
                      <TableRow key={inquiry.id}>
                        <TableCell align="right">{inquiry.id}</TableCell>
                        <TableCell align="right">
                          {inquiry.dependent 
                            ? `${inquiry.dependent.firstName} ${inquiry.dependent.lastName}`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          ₪{inquiry.decisionAmount?.toLocaleString() || '0'}
                        </TableCell>
                        <TableCell align="right">{inquiry.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

